/**
 * Keep patient on the Live Assist page after QR scan.
 *
 * Locked:
 * - Browser Back / swipe-back (history trap)
 * - Accidental navigation away
 * - Tab close / refresh → browser asks to stay (cannot fully block Close Tab — OS/browser rule)
 *
 * Escape:
 * - Patient confirms Leave on the browser dialog
 * - Or clears site data / force-closes the app
 * - Google Lens temporary tabs still need "Open in Chrome" / Home Screen
 */

import { APP_DISPLAY_NAME, isPatientFacingPath } from './patientRoomAccess';

const GUARD_DEPTH = 12;
const REARM_MS = 500;

let leaveAllowedUntil = 0;

/** Allow the next real navigation/unload without the stay prompt (rare). */
export function allowPatientLeaveOnce(ms = 4000) {
    leaveAllowedUntil = Date.now() + Math.max(500, ms);
}

export function isPatientLeaveAllowed() {
    return Date.now() < leaveAllowedUntil;
}

export function installPatientBackGuard({
    stickyHref,
    onBackAttempt,
    depth = GUARD_DEPTH,
    warnOnClose = true,
} = {}) {
    if (typeof window === 'undefined' || !window.history || !window.history.pushState) {
        return () => { };
    }

    let active = true;
    const stackDepth = Math.max(4, Math.min(24, Number(depth) || GUARD_DEPTH));
    let lockedHref = stickyHref
        || `${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`;

    const armStack = () => {
        if (!active) {
            return;
        }
        try {
            window.history.replaceState({ liveassistGuard: 1, root: 1 }, '', lockedHref);
            for (let i = 0; i < stackDepth; i += 1) {
                window.history.pushState({ liveassistGuard: 1, i }, '', lockedHref);
            }
        } catch (_e) {
            /* ignore */
        }
    };

    /** Update lock target after intentional in-app route change (bed → call → feedback). */
    const updateStickyHref = (href) => {
        if (!href) {
            return;
        }
        lockedHref = href;
        armStack();
    };

    armStack();

    const onPopState = () => {
        if (!active) {
            return;
        }
        // Always force back onto the locked patient URL
        armStack();
        if (typeof onBackAttempt === 'function') {
            try {
                onBackAttempt();
            } catch (_e) {
                /* ignore */
            }
        }
    };

    const onPageShow = (event) => {
        if (!active) {
            return;
        }
        armStack();
        // Returning from bfcache — stay locked
        if (event && event.persisted) {
            armStack();
        }
    };

    const onBeforeUnload = (event) => {
        if (!active || !warnOnClose) {
            return undefined;
        }
        if (isPatientLeaveAllowed()) {
            return undefined;
        }
        // Modern browsers show a generic confirm; returnValue is required to trigger it
        event.preventDefault();
        event.returnValue = `Stay on ${APP_DISPLAY_NAME}?`;
        return event.returnValue;
    };

    const rearmTimer = window.setInterval(() => {
        if (!active) {
            return;
        }
        try {
            const state = window.history.state;
            const pathOnly = window.location.pathname || '';
            const pathNow = `${pathOnly}${window.location.search || ''}${window.location.hash || ''}`;
            if (pathNow !== lockedHref) {
                // Intentional in-app patient navigation (bed → call → feedback): follow it
                if (isPatientFacingPath(pathOnly)) {
                    lockedHref = pathNow;
                    armStack();
                } else if (!isPatientLeaveAllowed()) {
                    // Accidental leave to another site/path — pull back into the locked room
                    try {
                        window.history.replaceState({ liveassistGuard: 1 }, '', lockedHref);
                    } catch (_e) {
                        /* ignore */
                    }
                    armStack();
                }
            } else if (!state || !state.liveassistGuard) {
                armStack();
            }
        } catch (_e) {
            /* ignore */
        }
    }, REARM_MS);

    window.addEventListener('popstate', onPopState);
    window.addEventListener('pageshow', onPageShow);
    if (warnOnClose) {
        window.addEventListener('beforeunload', onBeforeUnload);
    }

    const cleanup = () => {
        active = false;
        window.clearInterval(rearmTimer);
        window.removeEventListener('popstate', onPopState);
        window.removeEventListener('pageshow', onPageShow);
        if (warnOnClose) {
            window.removeEventListener('beforeunload', onBeforeUnload);
        }
    };
    cleanup.updateStickyHref = updateStickyHref;
    return cleanup;
}

/** Drop call/feedback URLs from history when landing on the stable bed page. */
export function lockPatientRoomUrl(href) {
    if (typeof window === 'undefined' || !window.history?.replaceState) {
        return;
    }
    const target = href || `${window.location.pathname}${window.location.search || ''}`;
    try {
        window.history.replaceState({ liveassistRoom: 1, liveassistGuard: 1 }, '', target);
    } catch (_e) {
        /* ignore */
    }
}
